import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import {
	BehaviorSubject,
	Observable,
	of,
	interval,
	debounce,
	mergeMap,
	Subscription,
	from,
	map,
} from 'rxjs';
import {
	MAT_DIALOG_DATA,
	MatDialogRef,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { Client } from '../../../models/client';
import { UserProfileService } from '../../../services/user-profile.service';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { MultiSelectTypeaheadComponent } from '../../../components/multi-select-typeahead/multi-select-typeahead.component';
import { MatDivider } from '@angular/material/list';

export type PropertyReportFilterOptions =
	Components.Schemas.PropertyReportFilterOptions;

export type PropertyReportFilters = {
	devices?: { serialNumber: string }[];
	deviceStatuses?: { deviceStatus: string }[];
	propertyNames?: { propertyName: string }[];
	postalCodes?: { postalCode: string }[];
	removalRequested?: { removalRequested: string }[];
	firstName: Client[];
	lastName: Client[];
	clientIdn: Client[];
};

export const emptyPropertyReportFilters = (): PropertyReportFilters => ({
	devices: [],
	deviceStatuses: [],
	propertyNames: [],
	postalCodes: [],
	removalRequested: [],
	firstName: [],
	lastName: [],
	clientIdn: [],
});

const DEVICE_STATUS_OPTIONS = [
	{ deviceStatus: 'inService' },
	{ deviceStatus: 'available' },
];

interface SearchConfig {
	type:
		| 'device'
		| 'deviceStatus'
		| 'property'
		| 'postalCode'
		| 'firstName'
		| 'lastName'
		| 'clientIdn';
	searchEvents: BehaviorSubject<string>;
	subscription: Subscription;
	selectedItems: Set<any>;
	options: any[];
	apiCall: (params: QueryParam[]) => Promise<any>;
	mapResponse: (item: any) => any;
	updateFilters: (items: any[]) => void;
}

@Component({
	selector: 'app-property-report-dialog',
	templateUrl: './property-report-dialog.component.html',
	styleUrls: [
		'./property-report-dialog.component.scss',
		'../../common/main.scss',
	],
	imports: [
		MatIconButton,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		LoadingIndicatorComponent,
		MultiSelectTypeaheadComponent,
		MatDivider,
		MatDialogActions,
		MatButton,
	],
})
export class PropertyReportDialogComponent implements AfterViewInit, OnDestroy {
	propertyReportFilters: PropertyReportFilters = emptyPropertyReportFilters();

	filterOptions?: PropertyReportFilterOptions;

	apiLoadingKeys = ApiLoadingKeys;

	devices?: { serialNumber: string }[] = [];
	deviceStatuses?: { deviceStatus?: string }[] = [];
	propertyNames?: { name: string }[] = [];
	postalCodes?: { postalCode: string }[] = [];
	removalRequested?: { removalRequested: string }[] = [];
	firstNames?: Client[] = [];
	lastNames?: Client[] = [];
	clientIdns?: Client[] = [];

	activeCompanyId = '';
	isSentrilock = false;
	isCareCompany = false;

	queryParams: QueryParam[] = [];

	deviceIds: string[] = [];
	deviceStatusIds: string[] = [];
	propertyNameIds: string[] = [];
	postalCodeIds: string[] = [];
	removalRequests: string[] = [];
	firstName: string[] = [];
	lastName: string[] = [];
	clientIdn: string[] = [];

	deviceMultiSelectorSearchPlaceHolder = $localize`:@@propertyReportDialog-deviceMultiSelectorSearchPlaceHolder:Search Serial Numbers (optional)`;
	deviceStatusMultiSelectorSearchPlaceHolder = $localize`:@@propertyReportDialog-deviceStatusMultiSelectorSearchPlaceHolder:Search Device Statuses (optional)`;
	propertyNamesMultiSelectorSearchPlaceHolder = $localize`:@@propertyReportDialog-propertyNameMultiSelectorSearchPlaceHolder:Search Properties (optional)`;
	postalCodesMultiSelectorSearchPlaceHolder = $localize`:@@propertyReportDialog-postalCodeMultiSelectorSearchPlaceHolder:Search Postal Codes (optional)`;
	removalRequestsMultiSelectorSearchPlaceHolder = $localize`:@@propertyReportDialog-removalRequestMultiSelectorSearchPlaceHolder:Search Removal Requested (optional)`;
	firstNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-firstNameMultiSelectSearchPlaceholder: Search Client First Names (optional)`;
	lastNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-lastNameMultiSelectSearchPlaceholder: Search Client Last Names (optional)`;
	clientIdnMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-clientIdnMultiSelectSearchPlaceholder: Search Client IDNs (optional)`;

	private readonly searchConfigs: { [key: string]: SearchConfig } = {
		device: {
			type: 'device',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.devices || [],
			apiCall: (params) => this.apiService.getAllDevices(params),
			mapResponse: (device) => ({
				serialNumber: device.device.serialNumber,
			}),
			updateFilters: (items) => {
				this.propertyReportFilters.devices = [...items];
			},
		},
		deviceStatus: {
			type: 'deviceStatus',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: DEVICE_STATUS_OPTIONS,
			apiCall: () => Promise.resolve(DEVICE_STATUS_OPTIONS),
			mapResponse: (status) => status,
			updateFilters: (items) => {
				this.propertyReportFilters.deviceStatuses = [...items];
			},
		},
		property: {
			type: 'property',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.propertyNames || [],
			apiCall: (params) => this.apiService.getProperties(params),
			mapResponse: (property) => ({
				propertyName: property.property.name,
			}),
			updateFilters: (items) => {
				this.propertyReportFilters.propertyNames = [...items];
			},
		},
		postalCode: {
			type: 'postalCode',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.postalCodes || [],
			apiCall: (params) => this.apiService.getPostalFilterOptions(params),
			mapResponse: (postal) => ({
				postalCode: postal.postalCode,
			}),
			updateFilters: (items) => {
				this.propertyReportFilters.postalCodes = [...items];
			},
		},
		firstName: {
			type: 'firstName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.firstNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.propertyReportFilters.firstName = [...items];
			},
		},
		lastName: {
			type: 'lastName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.lastNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.propertyReportFilters.lastName = [...items];
			},
		},
		clientIdn: {
			type: 'clientIdn',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.clientIdns || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.propertyReportFilters.clientIdn = [...items];
			},
		},
	};

	constructor(
		private readonly dialogRef: MatDialogRef<PropertyReportDialogComponent>,
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly userProfileService: UserProfileService,
		@Inject(MAT_DIALOG_DATA)
		public data?: {
			reportFilters: PropertyReportFilters;
			filterOptions: PropertyReportFilterOptions;
		}
	) {
		this.initializeData();
		Object.keys(this.searchConfigs).forEach((key) => this.setupSearch(key));
	}

	private initializeData(): void {
		this.filterOptions = this.data?.filterOptions;
		if (this.data?.reportFilters) {
			this.propertyReportFilters = this.data.reportFilters;

			// Initialize device options
			if (this.propertyReportFilters.devices?.length) {
				this.propertyReportFilters.devices.forEach((device) => {
					const deviceOption = { serialNumber: device.serialNumber };
					this.deviceIds.push(device.serialNumber);
					this.searchConfigs.device.selectedItems.add(deviceOption);
				});
				this.devices = Array.from(this.searchConfigs.device.selectedItems);
			}

			// Initialize status options
			if (this.propertyReportFilters.deviceStatuses?.length) {
				this.propertyReportFilters.deviceStatuses.forEach((status) => {
					const statusOption = { deviceStatus: status.deviceStatus };
					this.deviceStatusIds.push(status.deviceStatus);
					this.searchConfigs.deviceStatus.selectedItems.add(statusOption);
				});
				this.deviceStatuses = Array.from(
					this.searchConfigs.deviceStatus.selectedItems
				);
			}

			// Initialize property options
			if (this.propertyReportFilters.propertyNames?.length) {
				this.propertyReportFilters.propertyNames.forEach((property) => {
					const propertyOption = { propertyName: property.propertyName };
					this.propertyNameIds.push(property.propertyName);
					this.searchConfigs.property.selectedItems.add(propertyOption);
				});
				this.propertyNames = Array.from(
					this.searchConfigs.property.selectedItems
				);
			}

			// Initialize postal code options
			if (this.propertyReportFilters.postalCodes?.length) {
				this.propertyReportFilters.postalCodes.forEach((postal) => {
					const postalOption = { postalCode: postal.postalCode };
					this.postalCodeIds.push(postal.postalCode);
					this.searchConfigs.postalCode.selectedItems.add(postalOption);
				});
				this.postalCodes = Array.from(
					this.searchConfigs.postalCode.selectedItems
				);
			}
		}

		this.checkCareCompany()
			.then(() => {})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		this.isCareCompany =
			userProfile?.activeRoleDetails?.company?.companyType === 'care';
	}

	private setupSearch(type: string): void {
		const config = this.searchConfigs[type];
		const searchResults = config.searchEvents.asObservable().pipe(
			debounce(() => interval(300)),
			mergeMap((searchTerm) => this.handleSearch(searchTerm, config))
		);

		config.subscription = searchResults.subscribe();
	}

	private handleSearch(
		searchTerm: string,
		config: SearchConfig
	): Observable<any[]> {
		if (['firstName', 'lastName', 'clientIdn'].includes(config.type)) {
			if (!searchTerm || searchTerm.length < 2) {
				switch (config.type) {
					case 'firstName':
						return of(this.firstNames || []);
					case 'lastName':
						return of(this.lastNames || []);
					case 'clientIdn':
						return of(this.clientIdns || []);
				}
			}
		} else {
			if (!searchTerm || searchTerm === '' || searchTerm.length < 3) {
				switch (config.type) {
					case 'device':
						return of(this.devices || []);
					case 'deviceStatus':
						return of(this.deviceStatuses || []);
					case 'property':
						return of(this.propertyNames || []);
					case 'postalCode':
						return of(this.postalCodes || []);
					default:
						return of([]);
				}
			}
		}

		const queryParameters: QueryParam[] = [
			{ param: this.getSearchParamName(config), value: searchTerm },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		return from(config.apiCall(queryParameters)).pipe(
			map((results) => {
				if (!results?.length) {
					return Array.from(config.selectedItems);
				}

				const newOptions = results.map(config.mapResponse);
				const mergedOptions = this.mergeWithSelected(
					newOptions,
					config.selectedItems
				);

				switch (config.type) {
					case 'device':
						this.devices = mergedOptions;
						break;
					case 'deviceStatus':
						this.deviceStatuses = mergedOptions;
						break;
					case 'property':
						this.propertyNames = mergedOptions;
						break;
					case 'postalCode':
						this.postalCodes = mergedOptions;
						break;
					case 'firstName':
						this.firstNames = mergedOptions;
						break;
					case 'lastName':
						this.lastNames = mergedOptions;
						break;
					case 'clientIdn':
						this.clientIdns = mergedOptions;
						break;
				}

				return mergedOptions;
			})
		);
	}

	private getSearchParamName(config: SearchConfig): string {
		switch (config.type) {
			case 'device':
				return 'serialNumber';
			case 'deviceStatus':
				return 'status';
			case 'firstName':
				return 'firstName';
			case 'lastName':
				return 'lastName';
			case 'clientIdn':
				return 'idn';
			case 'property':
				return 'propertyName';
			case 'postalCode':
				return 'postalCode';
			default:
				return '';
		}
	}

	private mergeWithSelected(newOptions: any[], selectedItems: Set<any>): any[] {
		if (!newOptions) newOptions = [];
		if (!selectedItems) return newOptions;

		const newOptionIds = new Set(newOptions.map((opt) => opt.id));
		let mergedOptions = [...newOptions];

		selectedItems.forEach((selected) => {
			if (newOptionIds.has(selected.id)) {
				// if the selected item is already in the new options list, remove it from mergedOptions
				mergedOptions = mergedOptions.filter((opt) => opt.id !== selected.id);
			}
		});

		return mergedOptions;
	}

	private getOptionId(option: any): string {
		return (
			option.serialNumber ||
			option.deviceStatus ||
			option.propertyName ||
			option.postalCode
		);
	}

	onSearchInput(type: string, searchTerm: string): void {
		this.searchConfigs[type].searchEvents.next(searchTerm);
	}

	onSelectedItemsChanged(type: string, items: any): void {
		if (!items) return;

		const config = this.searchConfigs[type];
		config.selectedItems = new Set(items);
		config.updateFilters(items);
		config.searchEvents.next('');
	}

	async ngAfterViewInit(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id ?? '';
		const sentrilockId = await this.apiService.getSentrilockCompanyId();
		this.isSentrilock = this.activeCompanyId === sentrilockId;

		this.deviceStatuses = DEVICE_STATUS_OPTIONS;
	}

	get hasFilters(): boolean {
		return (
			JSON.stringify(this.propertyReportFilters) !==
			JSON.stringify(emptyPropertyReportFilters())
		);
	}

	get primaryButtonText(): string {
		return this.hasFilters
			? $localize`:@@propertyReportDialog-generate:Generate`
			: $localize`:@@propertyReportDialog-generateWithoutFilters:Generate without Filters`;
	}

	onSelectedDeviceChangedEvent(device: any): void {
		if (device === undefined) {
			return;
		}
		this.propertyReportFilters.devices = [...device];
	}

	onSelectedStatusChangedEvent(deviceStatuses: any): void {
		if (deviceStatuses === undefined) {
			return;
		}
		this.propertyReportFilters.deviceStatuses = [...deviceStatuses];
	}

	onSelectedPropertyChangedEvent(properties: any): void {
		if (properties === undefined) {
			return;
		}
		this.propertyReportFilters.propertyNames = [...properties];
	}

	onSelectePostalCodeChangedEvent(postalCodes: any): void {
		if (postalCodes === undefined) {
			return;
		}
		this.propertyReportFilters.postalCodes = [...postalCodes];
	}

	onSelecteRemovalRequestChangedEvent(removalRequests: any): void {
		if (removalRequests === undefined) {
			return;
		}
		this.propertyReportFilters.removalRequested = [...removalRequests];
	}

	onCloseDialog(): void {
		this.dialogRef.close();
	}

	async onGenerateReport(): Promise<void> {
		const compiledFilters = this.compileFilters();
		this.router
			.navigateByUrl(`reports/property-report-table`, {
				state: {
					reportFilters: this.propertyReportFilters,
					compiledFilters,
					hasFilters: this.hasFilters,
					filterOptions: this.filterOptions,
				},
			})
			.then(() => {
				this.dialogRef.close();
			});
	}

	compileFilters(): QueryParam[] {
		const queryParameters: QueryParam[] = [];

		if (
			this.propertyReportFilters.devices &&
			this.propertyReportFilters.devices.length > 0
		) {
			queryParameters.push({
				param: 'deviceSerialNumber',
				value: this.propertyReportFilters.devices.map((it) => it.serialNumber),
			});
		}
		if (
			this.propertyReportFilters.deviceStatuses &&
			this.propertyReportFilters.deviceStatuses.length > 0
		) {
			queryParameters.push({
				param: 'deviceStatuses',
				value: this.propertyReportFilters.deviceStatuses.map(
					(it) => it.deviceStatus
				),
			});
		}
		if (
			this.propertyReportFilters.firstName &&
			this.propertyReportFilters.firstName.length > 0
		) {
			queryParameters.push({
				param: 'firstNames',
				value: this.propertyReportFilters.firstName.map(
					(it) => it.firstName || ''
				),
			});
		}
		if (
			this.propertyReportFilters.lastName &&
			this.propertyReportFilters.lastName.length > 0
		) {
			queryParameters.push({
				param: 'lastNames',
				value: this.propertyReportFilters.lastName.map(
					(it) => it.lastName || ''
				),
			});
		}
		if (
			this.propertyReportFilters.clientIdn &&
			this.propertyReportFilters.clientIdn.length > 0
		) {
			queryParameters.push({
				param: 'clientIdns',
				value: this.propertyReportFilters.clientIdn.map((it) => it.idn || ''),
			});
		}
		if (
			this.propertyReportFilters.propertyNames &&
			this.propertyReportFilters.propertyNames.length > 0
		) {
			queryParameters.push({
				param: 'propertyNames',
				value: this.propertyReportFilters.propertyNames.map(
					(it) => it.propertyName
				),
			});
		}
		if (
			this.propertyReportFilters.postalCodes &&
			this.propertyReportFilters.postalCodes.length > 0
		) {
			queryParameters.push({
				param: 'postalCodes',
				value: this.propertyReportFilters.postalCodes.map(
					(it) => it.postalCode
				),
			});
		}

		// Commented out because not in AC

		// if (this.propertyReportFilters.removalRequested && this.propertyReportFilters.removalRequested.length > 0) {
		//   queryParameters.push(
		//       {
		//         param: 'removalRequested',
		//         value: this.propertyReportFilters.removalRequested.map(it => it.removalRequested)
		//       });
		// }

		return queryParameters;
	}

	ngOnDestroy(): void {
		Object.values(this.searchConfigs).forEach((config) => {
			if (config.subscription) {
				config.subscription.unsubscribe();
			}
		});
	}
}
